export const BUILD_STYLES = ['Solid Timber', 'Timber Framed', 'Steel Framed', 'StrongHaus'];
export const BUILT_HOME_UNASSIGNED_FILTER = 'Unassigned';
export const BUILD_STYLE_FILTERS = [...BUILD_STYLES, BUILT_HOME_UNASSIGNED_FILTER];
export const BUILT_HOME_TYPES = [
  'Design & Build',
  'Kitset',
  'Spec Build',
  'Repair/Alteration',
  'Transportable',
  'Show Home',
  'Commercial',
];
export const BUILT_HOME_TYPE_FILTERS = [...BUILT_HOME_TYPES, BUILT_HOME_UNASSIGNED_FILTER];

export const BUILT_HOME_SORTS = {
  LATEST_LISTED: 'latest-listed',
  OLDEST_LISTED: 'oldest-listed',
  LATEST_BUILT: 'latest-built',
  OLDEST_BUILT: 'oldest-built',
};

export const BUILT_HOME_REVIEW_FILTERS = {
  ALL: 'all',
  NEEDS_REVIEW: 'needs-review',
  REVIEWED: 'reviewed',
};

export const BUILT_HOME_MAP_REVIEW_FILTERS = [
  { value: BUILT_HOME_REVIEW_FILTERS.REVIEWED, label: 'Reviewed' },
  { value: BUILT_HOME_REVIEW_FILTERS.NEEDS_REVIEW, label: 'Unreviewed' },
];

export function getBuiltHomeReviewFilter(home) {
  return home?.needsReview === 1
    ? BUILT_HOME_REVIEW_FILTERS.NEEDS_REVIEW
    : BUILT_HOME_REVIEW_FILTERS.REVIEWED;
}

export function getBuiltHomeAssignmentFilter(value) {
  return String(value || '').trim() || BUILT_HOME_UNASSIGNED_FILTER;
}

export function normalizeAddress(value) {
  return String(value || '')
    .normalize('NFKD')
    .toLowerCase()
    .replace(/\b(new zealand|nz)\b/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/\s+/g, ' ');
}

export function formatDateInput(value) {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

export function parseDateInput(value) {
  const trimmed = String(value || '').trim();
  if (!trimmed) return null;

  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(trimmed);
  if (!match) throw new Error('Consent date must be a valid date.');

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));

  if (
    date.getUTCFullYear() !== year
    || date.getUTCMonth() !== month - 1
    || date.getUTCDate() !== day
  ) {
    throw new Error('Consent date must be a valid date.');
  }

  return date;
}

export function sortBuiltHomes(homes, sortOrder) {
  const getListedTime = (home) => home.createdAt ? new Date(home.createdAt).getTime() : 0;
  const getBuiltTime = (home) => {
    const year = Number(home.completedYear);
    if (Number.isFinite(year) && year > 0) {
      return Date.UTC(year, 0, 1);
    }

    if (!home.consentedDate) return null;
    const consentedTime = new Date(home.consentedDate).getTime();
    return Number.isFinite(consentedTime) ? consentedTime : null;
  };

  return [...homes].sort((a, b) => {
    if (sortOrder === BUILT_HOME_SORTS.LATEST_LISTED) {
      return getListedTime(b) - getListedTime(a);
    }

    if (sortOrder === BUILT_HOME_SORTS.OLDEST_LISTED) {
      return getListedTime(a) - getListedTime(b);
    }

    const aBuiltTime = getBuiltTime(a);
    const bBuiltTime = getBuiltTime(b);

    if (aBuiltTime === null && bBuiltTime !== null) return 1;
    if (aBuiltTime !== null && bBuiltTime === null) return -1;
    if (aBuiltTime === null && bBuiltTime === null) return getListedTime(b) - getListedTime(a);

    const dateDifference = sortOrder === BUILT_HOME_SORTS.OLDEST_BUILT
      ? aBuiltTime - bBuiltTime
      : bBuiltTime - aBuiltTime;

    return dateDifference || getListedTime(b) - getListedTime(a);
  });
}

export function formatCoordinates(lat, lng) {
  if (lat === null || lat === undefined || lng === null || lng === undefined) {
    return '';
  }

  return `${lat},${lng}`;
}

export function parseCoordinates(value) {
  const trimmedValue = String(value || '').trim();

  if (!trimmedValue) {
    return null;
  }

  const parts = trimmedValue.split(',');
  if (parts.length !== 2) {
    throw new Error('Coordinates must be in the format latitude,longitude.');
  }

  const latitude = Number(parts[0].trim());
  const longitude = Number(parts[1].trim());

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    throw new Error('Coordinates must be valid numbers in the format latitude,longitude.');
  }

  if (latitude < -90 || latitude > 90 || longitude < -180 || longitude > 180) {
    throw new Error('Coordinates are outside the valid latitude/longitude range.');
  }

  return { latitude, longitude };
}

export function toMapCoordinates(coordinates) {
  if (!coordinates) {
    return null;
  }

  return {
    lat: coordinates.latitude,
    lng: coordinates.longitude,
  };
}

export function getCoordinateSaveAction({
  previousAddress,
  nextAddress,
  coordinatesEdited,
  manualCoordinates,
  hasPreviousCoordinates,
}) {
  if (coordinatesEdited && manualCoordinates) return 'manual';

  const addressChanged = normalizeAddress(previousAddress) !== normalizeAddress(nextAddress);
  if (nextAddress && (addressChanged || coordinatesEdited || !hasPreviousCoordinates)) return 'geocode';
  if (hasPreviousCoordinates) return 'preserve';
  return 'clear';
}

const REDUNDANT_CONSENT_SET_WARNING = 'More than one plausible consent plan set';

export function cleanImportWarnings(warnings) {
  if (!Array.isArray(warnings)) return [];
  return [...new Set(warnings
    .map((warning) => String(warning || '').trim())
    .filter((warning) => warning && !warning.startsWith(REDUNDANT_CONSENT_SET_WARNING)))];
}
